import {DOCUMENT} from '@angular/common';
import {AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {select, Store} from '@ngrx/store';
import {fromEvent, Observable, Subject, Subscription} from 'rxjs';
import {auditTime, debounceTime, delay, takeUntil} from 'rxjs/operators';
import {SearchFilter, Value} from '../../../shared/components/advanced-searchbox/advanced-searchbox.component';
import {I18nDictionaryComponent} from '../../../shared/components/i18n-dictionary/i18n-dictionary.component';
import {TitleService} from '../../../shared/services/title.service';
import {WINDOW} from '../../../shared/services/window.service';
import * as CollectionActions from '../../actions/collection.actions';
import {Attribute, AttributeInputType} from '../../models/attribute';
import {Product, ProductContainer} from '../../models/product';
import {LoadCollectionCommand} from '../../models/product-commands';
import {SortDirection, SortField, SortOrder} from '../../../shared/models/magento-search-filter';
import * as fromProducts from '../../reducers';
import * as LayoutActions from '../../../core/actions/layout.actions';
import * as ProductActions from '../../actions/product.actions';
import {CollectionInitializationData} from '../../reducers';
import {AttributesInitializationState} from '../../reducers/attributes.reducer';
import {GalleryItem, ImageItem} from 'ng-gallery';
import {getIsAdvancedSearchOpen} from '../../../reducers';
import * as fromQuote from '../../../quotes/reducers';
import * as QuoteActions from '../../../quotes/actions/quote.action';
import * as fromLayout from '../../../reducers/index'
import { PriceHintService } from 'src/app/core/services/price-hint.service';

@Component({
    selector: 'app-collection-page',
    templateUrl: './collection-page.component.html',
    styleUrls: ['./collection-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CollectionPageComponent implements OnInit, OnDestroy, AfterViewInit, Navigable {
    static readonly COMPONENT_ID = 'CollectionPageComponent';

    readonly titleTextId = 'collection-page_title';

    products$: Observable<Product[]>;
    attributes$: Observable<Attribute[]>;
    isLoading$: Observable<boolean>;
    currentPage$: Observable<number>;
    pageSize$: Observable<number>;
    searchFilters$: Observable<SearchFilter[]>;
    sortOrders$: Observable<SortOrder[]>;
    isAdvancedSearchOpen$: Observable<boolean>;
    totalProductCount$: Observable<number>;

    previewImages: GalleryItem[];

    @ViewChild(I18nDictionaryComponent, { static: true })
    i18nDictionary: I18nDictionaryComponent;

    productsFlexFillUp$: EventEmitter<number> = new EventEmitter<number>();
    scrollToTopButtonVisible$ = new EventEmitter<boolean>();

    private onRouteChanges$: Subscription;
    private onWindowResize$: Subscription;
    private onWindowScroll$: Subscription;
    private onProductsChanged$: Subscription;
    private onCollectionInitializationData$: Subscription;
    private unsubscribeCollectionInitialization$: Subject<any>;

    static calculateProductsFlexFillUp(): number {
        const itemWidth = 200;
        const maxItems = 50;
        const count = Math.floor(Math.min(Math.max(document.body.clientHeight, document.body.clientWidth) / itemWidth, maxItems));
        console.log('products flex fill up -> ' + count);
        return count;
    }

    constructor(private store: Store<fromProducts.State>,
                private activatedRoute: ActivatedRoute,
                private router: Router,
                @Inject(DOCUMENT) private document: Document,
                @Inject(WINDOW) private window: Window,
                private titleService: TitleService,
                private quoteStore: Store<fromQuote.State>,
                private layoutstore: Store<fromLayout.State>,
                public priceHintService: PriceHintService
                ) {
    }

    ngOnInit() {
        this.products$ = this.store.pipe(select(fromProducts.getProductCollection));
        this.attributes$ = this.store.pipe(select(fromProducts.getAttributes));
        this.isLoading$ = this.store.pipe(select(fromProducts.getIsCollectionLoading));
        this.currentPage$ = this.store.pipe(select(fromProducts.getCurrentPage));
        this.pageSize$ = this.store.pipe(select(fromProducts.getPageSize));
        this.searchFilters$ = this.store.pipe(select(fromProducts.getSearchFilters));
        this.sortOrders$ = this.store.pipe(select(fromProducts.getSortOrders));
        this.totalProductCount$ = this.store.pipe(select(fromProducts.getTotalProductCount));
        this.isAdvancedSearchOpen$ = this.store.pipe(select(getIsAdvancedSearchOpen));

        this.onProductsChanged$ = this.products$
            .pipe(delay(0))
            .subscribe(() => this.determineProductsFlexFillUpContainer());
        this.onWindowResize$ = fromEvent(this.window, 'resize')
            .pipe(debounceTime(200))
            .subscribe(() => this.determineProductsFlexFillUpContainer());
        this.onWindowScroll$ = fromEvent(this.window, 'scroll')
            .pipe(auditTime(200))
            .subscribe(() => this.determineScrollToTopButtonVisibility());
        this.scrollToTopButtonVisible$.emit(false);

        this.onRouteChanges$ = this.activatedRoute.params.subscribe(params => {
            this.unsubscribeOnCollectionInitializationData();
            this.unsubscribeCollectionInitialization$ = new Subject<any>();

            this.onCollectionInitializationData$ = this.store.pipe(
                select(fromProducts.getCollectionInitializationData),
                takeUntil(this.unsubscribeCollectionInitialization$)
            ).subscribe(cid => this.initializeCollection(params, cid));
        });

        this.previewImages = [
            new ImageItem({ src: './assets/images/catalogue/HB_B2B_Catalogue_1.jpg' }),
            new ImageItem({ src: './assets/images/catalogue/HB_B2B_Catalogue_2.jpg' }),
            new ImageItem({ src: './assets/images/catalogue/HB_B2B_Catalogue_3.jpg' })
        ];
    }

    ngAfterViewInit(): void {
        this.titleService.setTitle(this.i18nDictionary.get(this.titleTextId));
    }

    determineScrollToTopButtonVisibility(): void {
        this.scrollToTopButtonVisible$.emit(this.window.scrollY > 500);
    }

    determineProductsFlexFillUpContainer(): void {
        this.productsFlexFillUp$.emit(CollectionPageComponent.calculateProductsFlexFillUp());
    }

    // noinspection JSMethodCanBeStatic
    hasMoreProductsToLoad(currentPage: number, pageSize: number, totalProductCount: number): boolean {
        return currentPage * pageSize < totalProductCount;
    }

    onScrollDown() {
        this.store.dispatch(CollectionActions.add({command: {}}));
    }

    onSearch(loadCollectionCommand: LoadCollectionCommand) {
        console.log('loadCollectionCommand:', loadCollectionCommand);
        this.store.dispatch(CollectionActions.load({command: loadCollectionCommand}));
    }

    ngOnDestroy(): void {
        this.onWindowResize$.unsubscribe();
        this.onWindowScroll$.unsubscribe();
        this.onProductsChanged$.unsubscribe();

        this.onRouteChanges$.unsubscribe();
        this.unsubscribeOnCollectionInitializationData();
    }

    openAddToQuoteDialog(product: Product) {
        // const dialogRef = this.dialog.open(AddProductDialogComponent, {
        //     width: '350px',
        //     data: {product: product}
        // });
        // dialogRef.afterClosed().subscribe(result => {
        //     console.log('The dialog was closed');
        // });
        this.store.dispatch(ProductActions.selectProduct(product));
        this.layoutstore.dispatch(LayoutActions.openQuoteList())
        this.quoteStore.dispatch(QuoteActions.openAddToQuoteMenu());
    }

    getProductSelector(id: number): Observable<ProductContainer> {
        return this.store.pipe(select(fromProducts.getProductContainerById(id)));
    }

    navigateToProduct(product: Product) {
        this.router.navigate(['/products', product.id]);
    }

    scrollToTop(): void {
        this.window.scrollTo({
            behavior: 'smooth',
            top: 0
        });
    }

    private unsubscribeOnCollectionInitializationData() {
        if (this.onCollectionInitializationData$ != null) {
            this.onCollectionInitializationData$.unsubscribe();
            this.unsubscribeCollectionInitialization$.unsubscribe();
        }
    }

    private initializeCollection(params: Params, initializationData: CollectionInitializationData): void {
        const searchData = params.searchdata;
        const filterData = params.filterdata;
        const sortData = params.sortdata;

        console.log('initialize product collection with initialization data, filter and sort params',
            initializationData, filterData, sortData);

        if (initializationData.attributeInitializationState === AttributesInitializationState.ERROR) {
            // can't be initialized! TODO dispatch General Error message
            return;
        } else if (initializationData.attributeInitializationState !== AttributesInitializationState.FINISHED) {
            console.log('attribute initialization state is not finished. Collection page can\'t be initialized yet');
            return;
        }

        const processedFilters = this.parseFilter(filterData, searchData, initializationData);
        const processedSortOrders = this.parseSortOrders(sortData);

        console.log('processed searchFilters, sortOrders', processedFilters, processedSortOrders);

        if (initializationData.currentPage === 0
            || JSON.stringify(initializationData.searchFilters) !== JSON.stringify(processedFilters)
            || JSON.stringify(initializationData.sortOrders) !== JSON.stringify(processedSortOrders)) {
            console.log('collection page will be initialized');
            this.onSearch({
                searchFilters: processedFilters != null ? processedFilters : initializationData.searchFilters,
                sortOrders: processedSortOrders != null ? processedSortOrders : initializationData.sortOrders
            });
        }

        this.unsubscribeCollectionInitialization$.next(undefined);
        this.unsubscribeCollectionInitialization$.complete();
    }

    private parseFilter(filterData: string, searchData: string, initializationData: CollectionInitializationData): SearchFilter[] {
        const processedFilters: SearchFilter[] = [];

        if (!!searchData) {
            processedFilters.push({
                model: 'advanced',
                value: this.parseFilterValue('advanced', searchData, initializationData)
            });
        }

        if (filterData == null) {
            // keep current filter data

            return [...processedFilters, ...initializationData.searchFilters];
        } else if (filterData === 'clear') {
            return processedFilters;
        } else {
            let previousFilter = null;

            for (let filter of filterData.split('_')) {
                console.log('process filter', filter);
                if (previousFilter != null) {
                    filter = previousFilter + '_' + filter;
                }

                const filterParts = filter.split(':', 2);
                if (filterParts.length < 2) {
                    previousFilter = filter;
                    continue;
                }
                previousFilter = null;

                const model = filterParts[0];
                const value = this.parseFilterValue(model, filterParts[1], initializationData);
                if (value == null) {
                    console.log('The filter value couldn\'t be parsed. Filter will be skipped', filter);
                    continue;
                }

                processedFilters.push({
                    model: model,
                    value: value
                });
            }

            if (previousFilter != null) {
                console.log('The filter can\'t be applied and will be skipped. You need to specify a value', previousFilter);
            }

            return processedFilters;
        }
    }

    private parseFilterValue(model: string, valueValue: string, initializationData: CollectionInitializationData): Value {
        const attribute = initializationData.attributes[model];
        if (attribute == null) {
            console.log(`The attribute for the filter model ${model} couldn't be found. Filter can't be applied`);
            return null;
        }

        if (attribute.inputType !== AttributeInputType.SELECT) {
            return {
                label: valueValue,
                value: valueValue
            };
        } else {
            const option = attribute.options.filter(s => s.value === valueValue);
            if (option.length === 0) {
                console.log('A suggestion for filter template model couldn\'t be found', model, valueValue);
                return null;
            }
            return option[0];
        }
    }

    private parseSortOrders(sortData: string): SortOrder[] {
        if (sortData == null) {
            // keep current sort order
            return [{field: SortField.SKU, direction: SortDirection.ASC}];
        } else if (sortData === 'clear') {
            return [];
        } else {
            const processedSortOrders: SortOrder[] = [];
            for (const sort of sortData.split('_')) {
                const sortParts = sort.split(':', 2);

                const field = SortField[sortParts[0].toUpperCase()];
                if (field == null) {
                    console.log('The sort field couldn\'t be found. Skip it', sort);
                    continue;
                }

                let direction;
                if (sortParts.length === 2) {
                    direction = SortDirection[sortParts[1].toUpperCase()];
                }
                if (direction == null) {
                    direction = SortDirection.ASC;
                }

                processedSortOrders.push({
                    field: field,
                    direction: direction
                });
            }
            return [{field: SortField.SKU, direction: SortDirection.ASC}];
        }
    }

    getId(): string {
        return CollectionPageComponent.COMPONENT_ID;
    }

    onOpenAdvancedSearch() {
        this.store.dispatch(LayoutActions.openAdvancedSearch());
    }

    onCloseAdvancedSearch() {
        this.store.dispatch(LayoutActions.closeAdvancedSearch());
    }
}
